import { z } from 'zod';
function stripDefault(schema) {
    if (schema instanceof z.ZodDefault || schema instanceof z.ZodPrefault)
        return stripDefault(schema.unwrap());
    return schema;
}
/**
 * `schema.partial()` as zod 3 meant it: every field optional, and a field
 * that was not sent is ABSENT from the output - never filled with its
 * create-time default. Returns a plain (stripping) object; chain `.strict()`
 * as every write schema does.
 */
export function patchOf(schema) {
    const shape = {};
    for (const [key, field] of Object.entries(schema.shape)) {
        shape[key] = stripDefault(field).optional();
    }
    return z.object(shape);
}
// ── zod 3's wording for zod 4's built-in messages ─────────────────────────
const quoted = (values) => values.map((v) => (typeof v === 'string' ? `'${v}'` : String(v))).join(' | ');
function received(input) {
    if (input === undefined)
        return 'undefined';
    if (input === null)
        return 'null';
    if (Array.isArray(input))
        return 'array';
    if (typeof input === 'number' && Number.isNaN(input))
        return 'nan';
    return typeof input;
}
const FORMAT_LABEL = {
    email: 'email',
    url: 'url',
    uuid: 'uuid',
    guid: 'uuid',
    datetime: 'datetime',
    date: 'date',
    time: 'time',
    regex: '',
    starts_with: 'input: must start with',
    ends_with: 'input: must end with',
    includes: 'input: must include',
};
/**
 * The message zod 3 gave for an issue, or undefined to keep zod 4's own (for
 * the few issue kinds zod 3 had no equivalent for). Exported for its test.
 */
export function zod3Message(issue) {
    switch (issue.code) {
        case 'invalid_type':
            if (issue.input === undefined)
                return 'Required';
            return `Expected ${String(issue.expected)}, received ${received(issue.input)}`;
        case 'too_small': {
            const min = String(issue.minimum);
            const exact = issue.exact === true;
            const inclusive = issue.inclusive !== false;
            switch (issue.origin) {
                case 'string':
                    return `String must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'over'} ${min} character(s)`;
                case 'array':
                case 'set':
                    return `Array must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'more than'} ${min} element(s)`;
                case 'number':
                case 'int':
                case 'bigint':
                    return `Number must be ${exact ? 'exactly equal to ' : inclusive ? 'greater than or equal to ' : 'greater than '}${min}`;
                case 'date':
                    return `Date must be ${exact ? 'exactly equal to ' : inclusive ? 'greater than or equal to ' : 'greater than '}${min}`;
                default:
                    return undefined;
            }
        }
        case 'too_big': {
            const max = String(issue.maximum);
            const exact = issue.exact === true;
            const inclusive = issue.inclusive !== false;
            switch (issue.origin) {
                case 'string':
                    return `String must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'under'} ${max} character(s)`;
                case 'array':
                case 'set':
                    return `Array must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'less than'} ${max} element(s)`;
                case 'number':
                case 'int':
                case 'bigint':
                    return `Number must be ${exact ? 'exactly' : inclusive ? 'less than or equal to' : 'less than'} ${max}`;
                case 'date':
                    return `Date must be ${exact ? 'exactly' : inclusive ? 'smaller than or equal to' : 'smaller than'} ${max}`;
                default:
                    return undefined;
            }
        }
        case 'invalid_value':
            if (issue.values.length === 1)
                return `Invalid literal value, expected ${JSON.stringify(issue.values[0])}`;
            return `Invalid enum value. Expected ${quoted(issue.values)}, received ${typeof issue.input === 'string' ? `'${issue.input}'` : String(issue.input)}`;
        case 'unrecognized_keys':
            return `Unrecognized key(s) in object: ${issue.keys.map((k) => `'${k}'`).join(', ')}`;
        case 'invalid_format': {
            const label = FORMAT_LABEL[issue.format];
            if (label === undefined)
                return `Invalid ${issue.format}`;
            if (label === '')
                return 'Invalid';
            if (label.startsWith('input:'))
                return `Invalid ${label} "${'prefix' in issue ? String(issue.prefix) : 'suffix' in issue ? String(issue.suffix) : 'includes' in issue ? String(issue.includes) : ''}"`;
            return `Invalid ${label}`;
        }
        case 'not_multiple_of':
            return `Number must be a multiple of ${String(issue.divisor)}`;
        case 'invalid_union':
        case 'custom':
            return 'Invalid input';
        default:
            return undefined;
    }
}
z.config({ customError: (issue) => zod3Message(issue) });
//# sourceMappingURL=zod-setup.js.map